import React, { useState, useMemo, useEffect } from "react";
import { CloseOutlined } from "src/icons";
import { Button } from "antd";
import { ReactComponent as FilterIcon } from "src/assets/filter-icon.svg";
import NS from "src/shared/utils/NS";
import { mergeStatuses } from "src/shared/utils";
import FilterSelect from "./FilterSelect";
import NSStatusHandler from "../NSStatusHandler";
import "./styles.scss";
import appFetch from "src/shared/fetch";

const fieldRelations: { [k: string]: string[] | undefined } = {
  city: ["city_zone", "city_area", "zone", "area"],
};
const childFields = Object.values(fieldRelations).flat() as string[];

type FieldValues = { [k: string]: { id: string; text: string }[] };

function useFilterValues(sliceId: number) {
  const formData = JSON.stringify({ slice_id: sliceId });
  const [[res, status], setResponse] = useState([[], new NS("INIT")]);

  useEffect(() => {
    setResponse([[], new NS("LOADING")]);
    appFetch
      .get("/api/superset/explore_json", {
        params: {
          form_data: formData,
        },
      })
      .then(({ response, error }) => {
        if (response) {
          setResponse([response.data, new NS("SUCCESS")]);
        } else if (error) {
          setResponse([[], new NS("ERROR")]);
        }
      })
      .catch((err) => {
        setResponse([[], new NS("ERROR")]);
      });
  }, []);

  return [res, status] as const;
}

function useChildFilterValues(sliceId: number, parentValues: SelectedFilterValues) {
  const [[res, status], setResponse] = useState<[FieldValues, NS]>([{}, new NS("INIT")]);
  const url = useMemo(() => {
    const empty = Object.values(parentValues).every((v) => v && v.length === 0);
    if (!sliceId || empty) return "";

    const formData = JSON.stringify({
      slice_id: sliceId,
      extra_filters: Object.entries(parentValues).map(([id, v]) => ({ col: id, op: "in", val: v })),
    });
    const queryparams = new URLSearchParams([["form_data", formData]]);
    return `/api/superset/explore_json?${queryparams}`;
  }, [sliceId, JSON.stringify(parentValues)]);

  useEffect(() => {
    if (url) {
      setResponse([{}, new NS("LOADING")]);
      appFetch
        .get(url, {})
        .then(({ response, error }) => {
          if (response) {
            setResponse([response.data, new NS("SUCCESS")]);
          } else if (error) {
            setResponse([{}, new NS("ERROR")]);
          }
        })
        .catch((err) => {
          console.error(err);
          setResponse([{}, new NS("ERROR")]);
        });
    }
  }, [url]);

  return [res, status] as const;
}

// since keys are dynamic, random key access return undefined
// at places where you're sure that random key access won't happen, pass <string[]> to type like in
export type SelectedFilterValues = { [k: string]: string[] };
type Props = {
  sliceId: number;
  onApply: (values: SelectedFilterValues) => void;
  initialValue: SelectedFilterValues;
  aggregationFields: { id: string; label: string }[];
  callerStatus: NS;
  onClose: () => void;
};
function FiltersPane({ sliceId, onApply, initialValue, onClose, aggregationFields, callerStatus }: Props) {
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilterValues>(() => initialValue);
  const [parentFieldValues, parentFieldValuesStatus] = useFilterValues(sliceId);

  const parentSelectedValues = Object.fromEntries(
    Object.keys(selectedFilters)
      .filter((id) => !childFields.includes(id))
      .map((id) => [id, selectedFilters[id]])
  );
  const [childFieldValues, childFieldValuesStatus] = useChildFilterValues(sliceId, parentSelectedValues);

  const handleFilterValueChange = (fieldId: string) => (values: string[]) => {
    setSelectedFilters({ ...selectedFilters, [fieldId]: values });
  };

  const clearFilters = () => setSelectedFilters({});

  const fieldValues = parentFieldValues;
  // `parentFieldValuesStatus.isSuccess` -> if child values comes before parent values, lets not show child values to user
  if (parentFieldValuesStatus.isSuccess && Object.values(childFieldValues).some((vs) => !!vs.length)) {
    // `childFields.forEach` -> childFieldValues contains parent values also.
    //    ignore them cuz parent values are filtered inside childFieldValues. But we need complete parent values always
    childFields.forEach((cid) => {
      if (childFieldValues[cid]) fieldValues[cid] = childFieldValues[cid];
    });
  }

  const areFiltersApplied = !!Object.values(selectedFilters).flat(1).length;
  const status = mergeStatuses(callerStatus, parentFieldValuesStatus);

  return (
    <section className="filters-pane">
      <section className="header">
        <h1>
          <FilterIcon className="filter-icon" />
          Filters
        </h1>
        <Button icon={<CloseOutlined className="text-lg" />} onClick={onClose}></Button>
      </section>
      <div className="content">
        <div className="clear-btn-wrapper">
          {areFiltersApplied && (
            <Button type="link" size="small" onClick={clearFilters}>
              CLEAR ALL
            </Button>
          )}
        </div>
        <NSStatusHandler status={status}>
          {() =>
            aggregationFields
              .filter(({ id }) => !childFields.includes(id) && fieldValues[id])
              .map(({ id, label }) => (
                <React.Fragment key={id}>
                  <FilterSelect
                    values={fieldValues[id]}
                    onChange={handleFilterValueChange(id)}
                    label={label}
                    selected={selectedFilters[id] || []}
                    loading={false}
                  />
                  {(selectedFilters[id] || []).length > 0 &&
                    (fieldRelations[id] || [])
                      .filter((cid) => fieldValues[cid])
                      .map((cid) => (
                        <FilterSelect
                          key={cid}
                          onChange={handleFilterValueChange(cid)}
                          values={fieldValues[cid]}
                          label={aggregationFields.find((f) => f.id === cid)?.label || cid}
                          selected={selectedFilters[cid] || []}
                          loading={childFieldValuesStatus.isLoading}
                        />
                      ))}
                </React.Fragment>
              ))
          }
        </NSStatusHandler>
      </div>
      <div className="footer">
        <Button
          type="primary"
          size="large"
          onClick={() => onApply(selectedFilters)}
          disabled={status.isLoading || status.isError}
          block
        >
          Apply
        </Button>
      </div>
    </section>
  );
}

export default FiltersPane;
